import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOffOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { validateSalesRegistration } from "../../../store/actions/salesActions";
import { salesRegister } from "../../../store/actions/salesActions";

const SalesRegister = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const miniSize = useMediaQuery("(max-width:330px)");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  const token = searchParams.get("token");
  // Properly decode email to handle + signs and other special characters
  // Replace spaces with + (URLs sometimes decode + as space)
  const emailParam = searchParams.get("email");
  const email = emailParam
    ? decodeURIComponent(emailParam).replace(/ /g, "+")
    : "";

  const validateRegistrationRef = useRef(null);

  useEffect(() => {
    if (!token || !email) {
      navigate("/login");
      return;
    }

    const handleValidate = async () => {
      const validRegistration = await dispatch(
        validateSalesRegistration(email, token)
      );

      if (!validRegistration) {
        navigate("/login");
        return;
      } else {
        setPageLoading(false);
      }
    };

    if (!validateRegistrationRef.current) {
      validateRegistrationRef.current = true;
      handleValidate();
    }
  }, [token, email, navigate, dispatch]);

  useEffect(() => {
    if (error !== "") {
      const timer = setTimeout(() => {
        setError("");
      }, 6000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [error]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: email || "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Email address must be a valid email")
        .required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values, helpers) => {
      try {
        setLoading(true);
        setError("");

        const result = await dispatch(
          salesRegister(
            values.email,
            values.password,
            values.confirmPassword,
            token
          )
        );

        if (result?.success) {
          // Registration successful - Redux state is already updated
          // Navigate to dashboard
          navigate("/sales/dashboard");
        } else {
          // Registration failed
          setError(result?.message || "Registration failed");
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        setError(err.response?.data?.message || "An error occurred");
      }
    },
  });

  return (
    <>
      {pageLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <CircularProgress color="warning" size="10rem" />
          </Box>
        </>
      ) : (
        <>
          {" "}
          <Box
            sx={
              mobileSize
                ? {
                    justifyContent: "center",
                    overflow: "auto",
                    minHeight: "100vh",
                    overflowY: "auto",
                    display: "flex",
                    mx: 0,
                    flexDirection: "column",
                    backgroundColor: "#fff",
                  }
                : {
                    backgroundImage: "url('/img/website_img/homeImg.jpeg')",
                    backgroundRepeat: "repeat",
                    minHeight: "100vh",
                    overflow: "hidden",
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    px: tabletSize ? 2 : 0,
                  }
            }
          >
            <Box
              sx={
                mobileSize
                  ? {
                      backgroundColor: "#fff",
                      width: "100%",
                      flexGrow: 1,
                      py: "1rem",
                    }
                  : {
                      backgroundColor: "#fff",
                      mt: 2,
                      maxWidth: 700,
                      borderRadius: "25px",
                      borderBottomLeftRadius: "0px",
                      borderBottomRightRadius: "0px",
                      px: tabletSize ? 2 : 3,
                      py: "10px",
                      width: "100%",
                      pt: "0.5rem",
                    }
              }
            >
              <Stack
                spacing={1}
                sx={{ mb: mobileSize ? 2 : 3, px: mobileSize ? 2 : 0 }}
              >
                <Box
                  sx={{ cursor: "pointer" }}
                  display="flex"
                  justifyContent="center"
                  onClick={() => {
                    navigate("/");
                  }}
                >
                  <img
                    style={{
                      width: miniSize ? "8rem" : "9rem",
                      marginLeft: "auto",
                      marginRight: "auto",
                      marginTop: mobileSize ? "0px" : "-9px",
                    }}
                    alt="Permit Rockstar Logo"
                    src="/img/website_img/PermitRockstar.png"
                  />
                </Box>

                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: mobileSize ? "1.1rem" : "1.25rem",
                    px: mobileSize ? 1 : 0,
                  }}
                  color="#004976"
                  variant="h6"
                >
                  Welcome to Permit Rockstar Sales Team
                </Typography>
                <Typography
                  sx={{
                    textAlign: "center",
                    lineHeight: mobileSize ? "1.5" : "3px",
                    pb: mobileSize ? "0.5rem" : "1rem",
                    px: mobileSize ? 1 : 0,
                  }}
                  color="text.secondary"
                  variant="body2"
                >
                  Please set up your password to complete your registration
                </Typography>
                {mobileSize && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <img
                      src="/img/website_img/pp_avatar_circle.png"
                      alt="Sales Team Avatar"
                      style={{
                        width: "60%",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
            <Box
              sx={
                !mobileSize
                  ? {
                      backgroundColor: "#fff",
                      mb: 2,
                      maxWidth: 700,
                      borderRadius: "25px",
                      borderTopLeftRadius: "0px",
                      borderTopRightRadius: "0px",
                      px: tabletSize ? 2 : 3,
                      py: "10px",
                      width: "100%",
                    }
                  : {
                      mb: 3,
                      backgroundColor: "#fff",
                      width: "100%",
                      py: mobileSize ? 2 : 0,
                    }
              }
            >
              <form
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
                style={
                  mobileSize
                    ? { paddingLeft: "1rem", paddingRight: "1rem" }
                    : tabletSize
                      ? { paddingLeft: "0.5rem", paddingRight: "0.5rem" }
                      : {}
                }
              >
                <Stack
                  spacing={
                    !!(formik.touched.email && formik.errors.email) &&
                    !!(formik.touched.password && formik.errors.password) &&
                    !!(
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    )
                      ? 1
                      : 2
                  }
                  sx={{ px: mobileSize ? 0 : 0 }}
                >
                  <TextField
                    error={!!(formik.touched.email && formik.errors.email)}
                    disabled={true}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    label="Email Address"
                    name="email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="email"
                    value={formik.values.email}
                  />
                  <TextField
                    error={
                      !!(formik.touched.password && formik.errors.password)
                    }
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    label="Password"
                    name="password"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.password}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseUp={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOffOutlined />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <TextField
                    error={
                      !!(
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      )
                    }
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                    label="Confirm Password"
                    name="confirmPassword"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.confirmPassword}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseUp={(e) => e.preventDefault()}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOffOutlined />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>

                {formik.errors.submit && (
                  <Typography color="error" sx={{ mt: 3 }} variant="body2">
                    {formik.errors.submit}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{
                    minHeight: "1.5rem",
                    fontSize: mobileSize ? "0.875rem" : "1rem",
                    px: mobileSize ? 1 : 0,
                  }}
                  textAlign="center"
                  my={1}
                  color="error"
                  fontWeight="bold"
                >
                  {error}
                </Typography>

                <LoadingButton
                  disabled={!token || !email}
                  loading={loading}
                  fullWidth
                  size="large"
                  sx={{
                    mt: mobileSize ? 1 : 0,
                    mb: mobileSize ? 2 : 0,
                    px: mobileSize ? 0 : 0,
                  }}
                  type="submit"
                  variant="contained"
                >
                  Complete Registration
                </LoadingButton>
              </form>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default SalesRegister;
